import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Radio, Trash2, Plus, Info, Pencil, AlertTriangle, Check, X } from 'lucide-react';
import * as routingApi from '@/api/notification-routing.api';
import type { NumeroAviso, RolNumero, RoutingConfig } from '@/api/notification-routing.api';
import * as settingsApi from '@/api/settings.api';
import type { MessagesConfig } from '@/api/installation.api';
import { ApiError } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';
import { normalizeAvisoPhone } from '@conversia/shared-types';
import { validateManualClient } from '@/lib/manual-client';
import {
  MESSAGE_TEMPLATE_CATALOG,
  TEMPLATE_SECTIONS,
  type MessageTemplateMeta,
  type TemplateSeccion,
} from '@/lib/message-templates';
import {
  ROL_LABELS,
  avisosPreRuteados,
  avisosQueUsanNumero,
  buscarFilas,
  destinoDeAviso,
  filasDeAvisos,
  rolesPreRuteadosEnPalabras,
  type Destino,
  type FilaAviso,
} from '@/lib/avisos-destinos';
import { cn } from '@/lib/cn';
import { Cargando } from '@/components/common/Cargando';

/**
 * Pestaña Mensajes: UNA SOLA PANTALLA DE AVISOS.
 *
 * Antes el interruptor de cada aviso vivía en su tarjeta de texto (arriba) y
 * sus números en una tabla al final: para saber si un aviso salía y a quién,
 * había que mirar dos lugares lejos uno del otro. Ahora arriba está el panel
 * de avisos —interruptor y números JUNTOS, agrupados por a quién le llegan— y
 * abajo quedan las tarjetas, que solo editan el TEXTO; cada fila tiene un
 * enlace «Editar texto» que baja hasta su tarjeta.
 *
 * Dos guardados, sin carreras: los TEXTOS viajan con el «Guardar cambios»
 * general de la página (config de instalación); los interruptores, números y
 * destinos viven en el Setting de ruteo y se guardan con su propio botón, que
 * aparece solo cuando hay cambios. El número de respaldo del dueño es un
 * tercer Setting y se guarda solo, en el acto.
 */

const ROL_OPTIONS = (Object.entries(ROL_LABELS) as [RolNumero, string][]).map(([value, label]) => ({
  value,
  label,
}));

const SECCIONES: TemplateSeccion[] = ['cliente', 'interno', 'tecnico'];

/** Orden en que se leen los grupos de avisos: primero lo que mira el dueño. */
const GRUPOS: TemplateSeccion[] = ['interno', 'cliente', 'tecnico'];

/** Id del ancla de cada tarjeta de texto, para el enlace «Editar texto». */
const anclaDePlantilla = (key: string) => `plantilla-${key}`;

export function MensajesTab({
  messages,
  onChange,
}: {
  messages: MessagesConfig;
  onChange: (patch: Partial<MessagesConfig>) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['notification-routing'],
    queryFn: routingApi.getRouting,
  });
  // El número de respaldo se lee ACÁ (antes solo se veía en Configuración →
  // Avisos): la pantalla tiene que poder decir a qué número cae un aviso sin
  // números propios, y dejar ponerlo si falta.
  const ownerAlerts = useQuery({
    queryKey: ['settings', 'ops-alerts'],
    queryFn: settingsApi.getOwnerAlerts,
  });

  const [routing, setRouting] = useState<RoutingConfig | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setRouting(data.config);
      setDirty(false);
    }
  }, [data]);

  const saveRouting = useMutation({
    mutationFn: (config: RoutingConfig) => routingApi.saveRouting(config),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-routing'] });
      toast.success('Avisos y ruteo guardados');
      setDirty(false);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar el ruteo'),
  });

  if (isLoading || !data || !routing) {
    return <Cargando />;
  }

  const canales = data.canales;
  const canalEmisor = canales.find((c) => c.status === 'CONNECTED') ?? canales[0] ?? null;
  const emisorVivo = canalEmisor?.status === 'CONNECTED';

  function isOn(trigger: string): boolean {
    return routing!.rutas[trigger]?.enabled !== false;
  }

  function setRuta(trigger: string, patch: Partial<{ enabled: boolean; numeroIds: string[] }>) {
    setRouting((r) =>
      r
        ? {
            ...r,
            rutas: {
              ...r.rutas,
              [trigger]: {
                enabled: r.rutas[trigger]?.enabled ?? true,
                numeroIds: r.rutas[trigger]?.numeroIds ?? [],
                ...patch,
              },
            },
          }
        : r,
    );
    setDirty(true);
  }

  return (
    <div className="space-y-8">
      {/* Cabecera: leyenda de variables + estado del canal emisor. */}
      <div className="space-y-2">
        <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Las palabras entre {'{{llaves}}'} se rellenan solas con los datos reales de cada caso.
          Usa los chips «+ variable» de cada mensaje para insertarlas.
        </p>
        <p
          className={cn(
            'flex items-center gap-2 rounded-lg px-3 py-2 text-sm',
            emisorVivo
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
          )}
        >
          <Radio className="h-4 w-4 shrink-0" />
          {canalEmisor ? (
            <span>
              Todo sale por el canal <b>{canalEmisor.name}</b> —{' '}
              {emisorVivo ? 'CONECTADO' : 'CAÍDO (ahora mismo nada puede salir)'}
            </span>
          ) : (
            <span>No hay canal de WhatsApp: ningún mensaje puede salir.</span>
          )}
        </p>
      </div>

      {/* Panel de avisos: interruptores y números, todo junto. */}
      <PanelDeAvisos
        routing={routing}
        ownerPhone={ownerAlerts.data?.owner_phone ?? ''}
        canalesPhones={new Set(canales.map((c) => c.phone).filter(Boolean))}
        isOn={isOn}
        setRuta={setRuta}
        onAddNumero={(n) => {
          setRouting((r) => (r ? { ...r, numeros: [...r.numeros, n] } : r));
          setDirty(true);
        }}
        onEditNumero={(id, patch) => {
          setRouting((r) =>
            r ? { ...r, numeros: r.numeros.map((n) => (n.id === id ? { ...n, ...patch } : n)) } : r,
          );
          setDirty(true);
        }}
        onRemoveNumero={(id) => {
          setRouting((r) =>
            r
              ? {
                  ...r,
                  numeros: r.numeros.filter((n) => n.id !== id),
                  rutas: Object.fromEntries(
                    Object.entries(r.rutas).map(([k, ruta]) => [
                      k,
                      { ...ruta, numeroIds: ruta.numeroIds.filter((nid) => nid !== id) },
                    ]),
                  ),
                }
              : r,
          );
          setDirty(true);
        }}
      />

      {/* Textos de los mensajes. El interruptor ya NO vive acá: vive arriba. */}
      <div className="space-y-6">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Textos de los mensajes
          </h2>
          <p className="mt-0.5 text-xs text-slate-400">
            Lo que dice cada mensaje. Para encenderlo, apagarlo o elegir a quién le llega, usa el
            panel de avisos de arriba.
          </p>
        </div>
        {SECCIONES.map((seccion) => {
          const meta = TEMPLATE_SECTIONS[seccion];
          const tarjetas = MESSAGE_TEMPLATE_CATALOG.filter((t) => t.seccion === seccion);
          return (
            <section
              key={seccion}
              className="space-y-3"
              data-guia={seccion === 'cliente' ? 'mensajes.plantillas' : undefined}
            >
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {meta.titulo}
                </h3>
                <p className="mt-0.5 text-xs text-slate-400">{meta.hint}</p>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                {tarjetas.map((t) => (
                  <TemplateCard
                    key={t.key}
                    meta={t}
                    value={messages[t.key]}
                    // Una plantilla OBLIGATORIA (sin trigger) siempre está activa:
                    // ver `trigger` en message-templates.
                    enabled={t.trigger ? isOn(t.trigger) : true}
                    obligatoria={t.trigger === null}
                    onText={(text) => onChange({ [t.key]: text } as Partial<MessagesConfig>)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Guardado del ruteo: aparece SOLO con cambios pendientes. */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <div className="flex items-center gap-3 rounded-full bg-white px-4 py-2 shadow-lg ring-1 ring-surface-border dark:bg-slate-900 dark:ring-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Cambios de switches y números sin guardar
            </span>
            <Button
              size="sm"
              onClick={() => saveRouting.mutate(routing)}
              loading={saveRouting.isPending}
            >
              Guardar avisos y ruteo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * EL panel de avisos: número de respaldo, directorio de números y todas las
 * filas (interruptor + destinos) agrupadas por a quién le llega cada aviso.
 */
function PanelDeAvisos({
  routing,
  ownerPhone,
  canalesPhones,
  isOn,
  setRuta,
  onAddNumero,
  onEditNumero,
  onRemoveNumero,
}: {
  routing: RoutingConfig;
  ownerPhone: string;
  canalesPhones: Set<string | null>;
  isOn: (trigger: string) => boolean;
  setRuta: (trigger: string, patch: Partial<{ enabled: boolean; numeroIds: string[] }>) => void;
  onAddNumero: (n: NumeroAviso) => void;
  onEditNumero: (id: string, patch: { nombre: string; rol: RolNumero }) => void;
  onRemoveNumero: (id: string) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  // El campo del número de respaldo lo abre también cada aviso que se quedó sin
  // destino, así que su estado vive acá y no adentro de la tarjeta.
  const [respaldoAbierto, setRespaldoAbierto] = useState(false);
  const respaldoRef = useRef<HTMLDivElement>(null);
  const filas = useMemo(() => filasDeAvisos(), []);
  const encontradas = buscarFilas(filas, busqueda);

  function toggleDestino(trigger: string, numeroId: string) {
    const actual = routing.rutas[trigger]?.numeroIds ?? [];
    setRuta(trigger, {
      numeroIds: actual.includes(numeroId)
        ? actual.filter((id) => id !== numeroId)
        : [...actual, numeroId],
    });
  }

  /** Lleva al dueño al campo del número de respaldo, en esta misma pantalla. */
  function irAlRespaldo() {
    setRespaldoAbierto(true);
    respaldoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <section className="space-y-3" data-guia="mensajes.a-quien">
      <div>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          ¿A quién le llegan los avisos?
        </h2>
        <p className="mt-0.5 text-xs text-slate-400">
          Cada aviso con su interruptor y sus números, todo en un solo lugar. Un aviso interno sin
          números marcados cae al número de respaldo.
        </p>
      </div>

      <Card>
        <CardBody className="space-y-5 p-4">
          <RespaldoDelDueno
            ownerPhone={ownerPhone}
            contenedorRef={respaldoRef}
            abierto={respaldoAbierto}
            onAbrir={setRespaldoAbierto}
          />

          <DirectorioNumeros
            routing={routing}
            canalesPhones={canalesPhones}
            onAddNumero={onAddNumero}
            onEditNumero={onEditNumero}
            onRemoveNumero={onRemoveNumero}
          />

          <NotaPreRuteados />

          <div className="space-y-3">
            <Input
              label="Buscar aviso"
              placeholder="Escribe parte del nombre: garantía, encuesta, técnico…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="max-w-sm"
            />
            {encontradas.length === 0 ? (
              <p className="rounded-md border border-dashed border-surface-border px-3 py-6 text-center text-sm text-slate-400 dark:border-slate-700">
                Ningún aviso se llama así. Prueba con otra palabra.
              </p>
            ) : (
              GRUPOS.map((seccion) => {
                const delGrupo = encontradas.filter((f) => f.seccion === seccion);
                if (delGrupo.length === 0) return null;
                return (
                  <div key={seccion} className="space-y-1.5">
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                      {TEMPLATE_SECTIONS[seccion].titulo}
                    </h3>
                    <div className="divide-y divide-surface-border rounded-md border border-surface-border dark:divide-slate-700 dark:border-slate-700">
                      {delGrupo.map((fila) => (
                        <FilaDeAviso
                          key={fila.trigger}
                          fila={fila}
                          enabled={isOn(fila.trigger)}
                          numeros={routing.numeros}
                          seleccion={routing.rutas[fila.trigger]?.numeroIds ?? []}
                          destino={destinoDeAviso(routing, fila.trigger, ownerPhone)}
                          onToggle={(next) => setRuta(fila.trigger, { enabled: next })}
                          onToggleDestino={(id) => toggleDestino(fila.trigger, id)}
                          onPonerRespaldo={irAlRespaldo}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardBody>
      </Card>
    </section>
  );
}

/**
 * El número de respaldo, VISIBLE y editable acá mismo. Es el último eslabón de
 * la cadena: un aviso interno sin números propios cae a este número, y si está
 * vacío no se envía. Antes solo se veía en otra pantalla (Configuración →
 * Avisos), que sigue funcionando: es el mismo Setting.
 */
function RespaldoDelDueno({
  ownerPhone,
  contenedorRef,
  abierto,
  onAbrir,
}: {
  ownerPhone: string;
  contenedorRef: React.RefObject<HTMLDivElement>;
  /** Abierto para editar (lo abre el botón de acá o un aviso sin destino). */
  abierto: boolean;
  onAbrir: (next: boolean) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phone, setPhone] = useState(ownerPhone);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPhone(ownerPhone);
  }, [ownerPhone]);

  // Al abrirse (venga de donde venga) el cursor ya está en el campo: quien
  // llegó desde un aviso sin destino solo tiene que escribir.
  useEffect(() => {
    if (abierto) inputRef.current?.focus();
  }, [abierto]);

  const save = useMutation({
    mutationFn: (valor: string) => settingsApi.saveOwnerAlerts({ owner_phone: valor }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'ops-alerts'] });
      toast.success('Número de respaldo guardado');
      onAbrir(false);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar el número'),
  });

  // Mismo criterio que en Configuración → Avisos: vacío es válido (significa
  // "sin avisos"), pero un número a medias se rechaza ANTES de guardar.
  function guardar() {
    if (phone.replace(/\D/g, '') === '') {
      setError(null);
      save.mutate('');
      return;
    }
    const normalizado = normalizeAvisoPhone(phone);
    if (!normalizado.ok) {
      setError(normalizado.error);
      return;
    }
    setError(null);
    setPhone(normalizado.phone);
    save.mutate(normalizado.phone);
  }

  const vacio = !ownerPhone;

  return (
    <div
      ref={contenedorRef}
      className={cn(
        'rounded-md border px-3 py-2.5',
        vacio
          ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
          : 'border-surface-border dark:border-slate-700',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {vacio ? <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" /> : null}
        <p
          className={cn(
            'text-sm',
            vacio ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-200',
          )}
        >
          {vacio ? (
            <>
              <b>Sin número de respaldo.</b> Los avisos internos que no tengan números propios no se
              envían.
            </>
          ) : (
            <>
              Número de respaldo (el del dueño): <b className="font-mono">{ownerPhone}</b>
            </>
          )}
        </p>
        {!abierto && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => onAbrir(true)}>
            <Pencil className="h-3.5 w-3.5" /> {vacio ? 'Poner número' : 'Cambiar'}
          </Button>
        )}
      </div>
      {abierto && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Input
            ref={inputRef}
            label="Número del dueño / soporte"
            placeholder="18295550000"
            className="w-48"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={error ?? undefined}
            hint="Con código de país. Vacío = no se envían avisos."
          />
          <Button size="sm" className="mb-0.5" onClick={guardar} loading={save.isPending}>
            Guardar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="mb-0.5"
            onClick={() => {
              setPhone(ownerPhone);
              setError(null);
              onAbrir(false);
            }}
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
}

/** La nota de los avisos que ya vienen dirigidos, armada de los defaults reales. */
function NotaPreRuteados() {
  const avisos = avisosPreRuteados();
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
      {/* Esta lista sale de ROLES_POR_DEFECTO (espejo del servidor). Antes era
          texto a mano y decía "tres" cuando ya eran cinco: no puede repetirse. */}
      <p>
        Estos {avisos.length} avisos ya vienen dirigidos a{' '}
        <strong>{rolesPreRuteadosEnPalabras()}</strong> sin que haga falta marcar nada. Van a los
        números cargados con esos roles; si marcas números a mano en alguno, mandan esos.
      </p>
      <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
        {avisos.map((a) => (
          <li key={a.trigger}>{a.titulo}</li>
        ))}
      </ul>
    </div>
  );
}

/** Directorio de números: alta, edición de nombre/rol y baja con confirmación. */
function DirectorioNumeros({
  routing,
  canalesPhones,
  onAddNumero,
  onEditNumero,
  onRemoveNumero,
}: {
  routing: RoutingConfig;
  canalesPhones: Set<string | null>;
  onAddNumero: (n: NumeroAviso) => void;
  onEditNumero: (id: string, patch: { nombre: string; rol: RolNumero }) => void;
  onRemoveNumero: (id: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [phone, setPhone] = useState('');
  const [rol, setRol] = useState<RolNumero>('otro');
  const [error, setError] = useState<string | null>(null);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [borrando, setBorrando] = useState<NumeroAviso | null>(null);

  function agregar() {
    const v = validateManualClient({ nombre, telefono: phone });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    // El chip guarda el número YA normalizado (con código de país): así el
    // badge "número del canal" compara manzanas con manzanas.
    const normalized = normalizeAvisoPhone(phone);
    if (!normalized.ok) {
      setError(normalized.error);
      return;
    }
    setError(null);
    onAddNumero({
      id: `n${Date.now().toString(36)}`,
      nombre: nombre.trim(),
      rol,
      phone: normalized.phone,
    });
    setNombre('');
    setPhone('');
    setRol('otro');
  }

  const avisosDelQueBorra = borrando ? avisosQueUsanNumero(routing, borrando.id) : [];

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        Números que reciben avisos
      </h3>
      {routing.numeros.length === 0 && (
        <p className="text-xs text-slate-400">
          Todavía no hay números cargados: todo cae al número de respaldo.
        </p>
      )}
      {routing.numeros.map((n) =>
        editandoId === n.id ? (
          <EditorDeNumero
            key={n.id}
            numero={n}
            onCancel={() => setEditandoId(null)}
            onSave={(patch) => {
              onEditNumero(n.id, patch);
              setEditandoId(null);
            }}
          />
        ) : (
          <div
            key={n.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-surface-border px-3 py-1.5 text-sm dark:border-slate-700"
          >
            <span className="font-medium text-slate-900 dark:text-slate-100">{n.nombre}</span>
            <Badge variant="info">{ROL_LABELS[n.rol]}</Badge>
            <span className="font-mono text-slate-500">{n.phone}</span>
            {canalesPhones.has(n.phone) && <Badge variant="success">número del canal</Badge>}
            <UsoDelNumero routing={routing} numeroId={n.id} />
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${n.nombre}`}
              className="ml-auto h-7 w-7 text-slate-400 hover:text-brand-600"
              onClick={() => setEditandoId(n.id)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Quitar ${n.nombre}`}
              className="h-7 w-7 text-slate-400 hover:text-red-600"
              onClick={() => setBorrando(n)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Input
          label="Nombre"
          placeholder="Secretaria"
          className="w-36"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
        <Input
          label="Teléfono"
          placeholder="18295550000"
          className="w-36"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Select
          label="Rol"
          className="w-44"
          options={ROL_OPTIONS}
          value={rol}
          onChange={(e) => setRol(e.target.value as RolNumero)}
        />
        <Button variant="outline" size="sm" className="mb-0.5" onClick={agregar}>
          <Plus className="h-4 w-4" /> Agregar
        </Button>
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}

      {/* Borrar un número no es solo sacarlo de la lista: lo saca de TODOS los
          avisos donde estaba marcado, y esos avisos pasan al respaldo. */}
      <Modal
        open={Boolean(borrando)}
        onClose={() => setBorrando(null)}
        title={`Quitar a ${borrando?.nombre ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setBorrando(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (borrando) onRemoveNumero(borrando.id);
                setBorrando(null);
              }}
            >
              Sí, quitar {borrando?.nombre}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            <b>{borrando?.nombre}</b> ({borrando?.phone}) deja de recibir avisos.
          </p>
          {avisosDelQueBorra.length > 0 ? (
            <div>
              <p>
                Se va a quitar de {avisosDelQueBorra.length} aviso
                {avisosDelQueBorra.length === 1 ? '' : 's'} donde estaba marcado:
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {avisosDelQueBorra.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
              <p className="mt-2">
                Los avisos que queden sin ningún número marcado pasan al número de respaldo.
              </p>
            </div>
          ) : (
            <p>No está marcado en ningún aviso.</p>
          )}
          <p className="text-xs text-slate-400">
            El cambio se aplica al guardar con «Guardar avisos y ruteo».
          </p>
        </div>
      </Modal>
    </div>
  );
}

/** Cuántos avisos tienen marcado este número (antes no se veía en ningún lado). */
function UsoDelNumero({ routing, numeroId }: { routing: RoutingConfig; numeroId: string }) {
  const usos = avisosQueUsanNumero(routing, numeroId);
  if (usos.length === 0) {
    return <span className="text-xs text-slate-400">sin avisos propios</span>;
  }
  return (
    <span className="text-xs text-slate-500 dark:text-slate-400" title={usos.join(', ')}>
      en {usos.length} aviso{usos.length === 1 ? '' : 's'}
    </span>
  );
}

/**
 * Edición de un número ya cargado. Se edita el NOMBRE y el ROL; el teléfono no,
 * porque cambiarlo en sitio redirige en silencio todos los avisos donde ese
 * número estaba marcado — para eso se quita (con su aviso) y se agrega el nuevo.
 */
function EditorDeNumero({
  numero,
  onSave,
  onCancel,
}: {
  numero: NumeroAviso;
  onSave: (patch: { nombre: string; rol: RolNumero }) => void;
  onCancel: () => void;
}) {
  const [nombre, setNombre] = useState(numero.nombre);
  const [rol, setRol] = useState<RolNumero>(numero.rol);
  const [error, setError] = useState<string | null>(null);

  function guardar() {
    if (nombre.trim().length < 2) {
      setError('Ponle un nombre de al menos 2 letras.');
      return;
    }
    onSave({ nombre: nombre.trim(), rol });
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border border-brand-300 px-3 py-2 dark:border-brand-700">
      <Input
        label="Nombre"
        className="w-36"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        error={error ?? undefined}
      />
      <Select
        label="Rol"
        className="w-44"
        options={ROL_OPTIONS}
        value={rol}
        onChange={(e) => setRol(e.target.value as RolNumero)}
      />
      <span className="mb-2.5 font-mono text-xs text-slate-400">{numero.phone}</span>
      <Button size="sm" className="mb-0.5" onClick={guardar}>
        <Check className="h-4 w-4" /> Listo
      </Button>
      <Button variant="ghost" size="sm" className="mb-0.5" onClick={onCancel}>
        <X className="h-4 w-4" /> Cancelar
      </Button>
    </div>
  );
}

/** Una fila del panel: título + interruptor + (si es interno) sus números. */
function FilaDeAviso({
  fila,
  enabled,
  numeros,
  seleccion,
  destino,
  onToggle,
  onToggleDestino,
  onPonerRespaldo,
}: {
  fila: FilaAviso;
  enabled: boolean;
  numeros: NumeroAviso[];
  seleccion: string[];
  destino: Destino;
  onToggle: (next: boolean) => void;
  onToggleDestino: (numeroId: string) => void;
  onPonerRespaldo: () => void;
}) {
  /** Baja hasta la tarjeta del texto de este aviso, en esta misma pantalla. */
  function editarTexto() {
    if (!fila.plantillaKey) return;
    const el = document.getElementById(anclaDePlantilla(fila.plantillaKey));
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <div className="px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Toggle
          checked={enabled}
          onChange={onToggle}
          label={`${fila.titulo}: encendido o apagado`}
        />
        <span
          className={cn(
            'text-sm text-slate-700 dark:text-slate-200',
            !enabled && 'text-slate-400 line-through',
          )}
        >
          {fila.titulo}
        </span>
        {fila.plantillaKey && (
          <button
            type="button"
            onClick={editarTexto}
            className="text-xs text-brand-600 hover:underline dark:text-brand-400"
          >
            Editar texto
          </button>
        )}
        {fila.ruteable ? (
          <span className="ml-auto flex flex-wrap items-center gap-1">
            {numeros.map((n) => (
              <button
                key={n.id}
                type="button"
                disabled={!enabled}
                onClick={() => onToggleDestino(n.id)}
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-40',
                  seleccion.includes(n.id)
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                )}
              >
                {n.nombre}
              </button>
            ))}
          </span>
        ) : (
          <span className="ml-auto text-xs text-slate-400">
            {fila.seccion === 'cliente' ? 'Le llega al cliente' : 'Le llega al técnico asignado'}
          </span>
        )}
      </div>
      {fila.ruteable && enabled && (
        <LineaDeDestino destino={destino} onPonerRespaldo={onPonerRespaldo} />
      )}
    </div>
  );
}

/** A dónde va HOY este aviso, dicho con nombre y número — nunca "→ dueño" a secas. */
function LineaDeDestino({
  destino,
  onPonerRespaldo,
}: {
  destino: Destino;
  onPonerRespaldo: () => void;
}) {
  if (destino.tipo === 'numeros') {
    return <p className="mt-1 text-xs text-slate-400">→ {destino.nombres.join(', ')}</p>;
  }
  if (destino.tipo === 'rol') {
    return (
      <p className="mt-1 text-xs text-slate-400">
        → viene dirigido a {destino.roles.map((r) => ROL_LABELS[r]).join(' y ')}:{' '}
        {destino.nombres.join(', ')}
      </p>
    );
  }
  if (destino.tipo === 'respaldo') {
    return (
      <p className="mt-1 text-xs text-slate-400">
        → número del dueño: <span className="font-mono">{destino.phone}</span>
      </p>
    );
  }
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs font-medium text-red-600 dark:text-red-400">
      <AlertTriangle className="h-3.5 w-3.5" />
      Sin números: este aviso no se envía
      <button type="button" onClick={onPonerRespaldo} className="underline">
        Poner el número de respaldo
      </button>
    </p>
  );
}

/** Tarjeta de UNA plantilla: título + editor del TEXTO + chips de variables. */
function TemplateCard({
  meta,
  value,
  enabled,
  obligatoria,
  onText,
}: {
  meta: MessageTemplateMeta;
  value: string;
  enabled: boolean;
  /** Plantilla que no se puede apagar (baja y re-alta). */
  obligatoria: boolean;
  onText: (text: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  /** Inserta el token en la posición del cursor (o al final). */
  function insert(token: string) {
    const el = ref.current;
    if (!el) {
      onText(`${value}${token}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onText(next);
    // Devuelve el foco tras el re-render, con el cursor después del token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  return (
    <Card
      id={anclaDePlantilla(meta.key)}
      className={cn('transition-opacity', !enabled && 'opacity-60')}
    >
      <CardBody className="space-y-2.5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{meta.titulo}</p>
            <p className="mt-0.5 text-xs leading-snug text-slate-400">{meta.cuando}</p>
          </div>
          {/* El estado se VE acá, pero se cambia arriba: un solo interruptor
              por aviso, en el panel donde también se eligen sus números. */}
          <span
            className={cn(
              'shrink-0 pt-0.5 text-[11px] font-medium',
              enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400',
            )}
            title={
              obligatoria
                ? 'Es la respuesta obligatoria al cliente: se puede cambiar el texto, no apagarla'
                : 'Se enciende o apaga en el panel de avisos, arriba'
            }
          >
            {obligatoria ? 'Siempre activo' : enabled ? 'Activo' : 'Apagado'}
          </span>
        </div>
        <Textarea
          ref={ref}
          aria-label={`Texto: ${meta.titulo}`}
          rows={meta.rows}
          value={value}
          onChange={(e) => onText(e.target.value)}
          className="text-sm"
        />
        {meta.variables.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[11px] text-slate-400">+ variable:</span>
            {meta.variables.map((v) => (
              <button
                key={v.token}
                type="button"
                title={`Inserta ${v.token}`}
                onClick={() => insert(v.token)}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
